import React, { useState, useRef } from 'react';
import { Upload, Download, X, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { investorContactAPI } from '../../services/api';

/**
 * InteractionBulkImportModal — Phase L4
 *
 * Two-step bulk-import flow specifically for INTERACTIONS (notes/meetings)
 * against existing investor contacts:
 *   1. Download a template pre-seeded with one row per existing contact
 *      carrying that contact's unique Reference No. + display name.
 *   2. User fills date/details/next-steps/lead per row (multiple rows per
 *      contact allowed for multiple interactions) and uploads back.
 *
 * Recipients are stored sorted by date DESC by the existing /interactions
 * GET endpoint so no extra ordering work is needed on the server side.
 */
const InteractionBulkImportModal = ({ isOpen, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  if (!isOpen) return null;

  const _validateFile = (selected) => {
    if (!selected) return false;
    const ok = selected.name.endsWith('.csv') || selected.name.endsWith('.xlsx');
    if (!ok) {
      alert('Please select a .csv or .xlsx file');
      return false;
    }
    return true;
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (_validateFile(selected)) {
      setFile(selected);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (_validateFile(dropped)) {
      setFile(dropped);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await investorContactAPI.downloadInteractionTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'investor_contact_interactions_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download interaction template:', err);
      alert('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const response = await investorContactAPI.bulkImportInteractions(file);
      setResult(response.data);
      if (response.data.success_count > 0) {
        onSuccess?.();
      }
    } catch (err) {
      console.error('Bulk import interactions failed:', err);
      const detail = err.response?.data?.detail;
      setResult({
        success_count: 0,
        error_count: 0,
        message: detail || 'Import failed. Please check your file format.',
        errors: [],
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
    onClose();
  };

  const succeeded = (result?.success_count || 0) > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-import-interactions-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-lg font-bold text-gray-900">Bulk Import Investor Contact Interactions</h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded" data-testid="close-interactions-bulk-import-modal">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Download Template */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-2">
              Step 1 — download the template. It comes pre-filled with one row per investor contact
              carrying that contact's <b>Reference No.</b> and name. Add interaction rows below the
              existing ones (copy the Reference No. for additional interactions on the same contact).
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center text-sm text-blue-700 font-medium hover:text-blue-900"
              data-testid="download-interactions-template-btn"
            >
              <Download size={16} className="mr-1" />
              Download Interactions Template (.xlsx)
            </button>
          </div>

          {/* File Upload Area */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-blue-400 bg-blue-50' : file ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            data-testid="interactions-file-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="interactions-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={24} className="text-green-600" />
                <div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500"
                  aria-label="Remove file"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600 font-medium">Drop file here or click to browse</p>
                <p className="text-sm text-gray-400 mt-1">.xlsx or .csv supported</p>
              </>
            )}
          </div>

          <div className="text-xs text-gray-500">
            <p className="font-medium mb-1">Required per row: Reference No., Date of Interaction</p>
            <p>Optional: Details, Next Steps, Investor Lead (User Email)</p>
            <p className="mt-1 text-gray-400">Multiple interactions per contact are allowed — just add additional rows with the same Reference No.</p>
          </div>

          {/* Upload Result */}
          {result && (
            <div className={`rounded-lg p-4 border ${succeeded ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="interactions-import-result">
              <div className="flex items-start gap-2">
                {succeeded ? (
                  <CheckCircle size={20} className="text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle size={20} className="text-red-600 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${succeeded ? 'text-green-800' : 'text-red-800'}`}>
                    {result.message
                      || (succeeded
                        ? `Imported ${result.success_count} interactions${result.error_count ? ` · ${result.error_count} skipped` : ''}.`
                        : `Could not import any interactions${result.error_count ? ` · ${result.error_count} errors` : ''}.`)}
                  </p>
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      <p className="text-sm font-medium text-red-700">Errors:</p>
                      {result.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-600">
                          Row {err.row}: {err.error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
          >
            {succeeded ? 'Done' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm inline-flex items-center"
            data-testid="upload-interactions-btn"
          >
            {uploading ? (
              <><Loader size={16} className="mr-2 animate-spin" />Importing...</>
            ) : (
              <><Upload size={16} className="mr-2" />Import Interactions</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractionBulkImportModal;
